
import { Link } from 'react-router-dom';

export default function LeaderRolePage() {
  const roleTransition = {
    wrong: {
      title: 'タスク管理者',
      icon: 'ri-close-circle-line',
      color: 'from-gray-400 to-gray-500',
      description: '指示を出し、進捗を管理し、評価する',
      characteristics: [
        '細かく指示を出す',
        '進捗を常に監視する',
        '問題を自分で解決する',
        '完璧を求める',
        'メンバーを評価する'
      ]
    },
    correct: {
      title: '相互作用の設計者',
      icon: 'ri-checkbox-circle-line',
      color: 'from-rose-500 to-pink-500',
      description: 'メンバー同士が協力し、学び合える環境をつくる',
      characteristics: [
        '役割と責任を明確にする',
        '心理的安全性を高める',
        'メンバーの自律性を尊重する',
        '学習の機会をつくる',
        '相互作用を促進する'
      ]
    }
  };

  const specificActions = [
    {
      number: '1',
      title: '自分の不完全さを認める（脆弱性の開示）',
      description: '「分からない」「間違えた」「助けてほしい」をリーダー自身が言う',
      icon: 'ri-user-heart-line',
      color: 'from-rose-600 to-pink-600',
      why: 'リーダーが完璧を装うと、メンバーも弱みを見せられなくなる。リーダーが脆弱性を開示することで、メンバーも安心して本音を話せるようになる。',
      practices: [
        'ミーティングで「私も分からないので、一緒に考えたい」と言う',
        '自分のミスを隠さずに共有する',
        'メンバーに助けを求める',
        '「完璧なリーダー」を演じない'
      ],
      examples: [
        '「この患者さんの対応、私も悩んでいます。みんなの意見を聞かせてください」',
        '「昨日、処方内容の確認を忘れてしまいました。今後どうすれば防げるか、一緒に考えてもらえますか」',
        '「在宅訪問の件、私一人では判断できません。〇〇さん、相談に乗ってもらえますか」'
      ]
    },
    {
      number: '2',
      title: '仕事を「学習の場」と定義する',
      description: '失敗を責めるのではなく、「何を学べるか」に焦点を当てる',
      icon: 'ri-book-open-line',
      color: 'from-pink-600 to-rose-500',
      why: '仕事を「評価の場」と捉えると、メンバーは失敗を恐れて挑戦しなくなる。「学習の場」と定義することで、失敗から学ぶ文化が生まれる。',
      practices: [
        'ヒヤリハットを「改善のチャンス」として扱う',
        '「なぜ起きたか」ではなく「どうすれば防げるか」を考える',
        '個人の責任追及ではなく、システム改善に焦点を当てる',
        '失敗から学んだことを共有する'
      ],
      examples: [
        '「このミスから何を学べるか、みんなで考えましょう」',
        '「誰が悪いかではなく、どうすれば防げるかを話し合いたいです」',
        '「今回の経験を次に活かすために、何ができるでしょうか」'
      ]
    },
    {
      number: '3',
      title: '会話の偏り（発言者固定）を是正する',
      description: '特定の人だけが話す状況を避け、全員が発言できる場をつくる',
      icon: 'ri-team-line',
      color: 'from-rose-500 to-pink-500',
      why: '一部の人だけが発言する状況では、多様な視点が失われ、チームの知恵が活かされない。全員が発言できる場をつくることで、チーム全体の知恵を引き出せる。',
      practices: [
        'ミーティングで発言していない人に意見を求める',
        '「他の人の意見も聞きたい」と促す',
        '少数意見を大切にする',
        '全員が発言する機会を設ける'
      ],
      examples: [
        '「〇〇さんはどう思いますか？」',
        '「まだ発言していない人の意見も聞きたいです」',
        '「少数意見かもしれませんが、大切な視点だと思います」'
      ]
    },
    {
      number: '4',
      title: '共有リーダーシップを許容する',
      description: 'リーダーが全てを決めるのではなく、メンバーにも判断を委ねる',
      icon: 'ri-user-star-line',
      color: 'from-pink-500 to-rose-400',
      why: 'リーダーが全てを決めると、メンバーの自律性が失われ、成長機会も奪われる。メンバーに判断を委ねることで、自律性と有能感が高まる。',
      practices: [
        '判断をメンバーに委ねる',
        '「あなたならどうする？」と問いかける',
        'メンバーの判断を尊重する',
        '失敗しても責めない'
      ],
      examples: [
        '「この件は〇〇さんに判断してもらいたいです」',
        '「あなたの判断を信頼しています」',
        '「結果がどうであれ、あなたの判断を尊重します」'
      ]
    },
    {
      number: '5',
      title: '多様性を前提として扱う',
      description: '「みんな同じ」ではなく、「それぞれ違う」を前提にする',
      icon: 'ri-group-line',
      color: 'from-rose-400 to-pink-400',
      why: '「みんな同じ」を前提にすると、少数派が声を上げにくくなる。「それぞれ違う」を前提にすることで、多様な視点が活かされる。',
      practices: [
        '働き方の違いを認める',
        '価値観の違いを尊重する',
        '強みの違いを活かす',
        '「普通」を押し付けない'
      ],
      examples: [
        '「それぞれの働き方があっていいと思います」',
        '「あなたの視点は私にはない発想で、とても参考になります」',
        '「みんな違って、みんないい」'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-rose-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-rose-600 transition-colors duration-200">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/team-building" className="hover:text-rose-600 transition-colors duration-200">チームビルディング</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-rose-600 font-medium">リーダーの役割</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-user-star-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Ⅳ．リーダー（薬局長・管理職）の役割
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              タスク管理者から相互作用の設計者へ
            </p>
          </div>
        </div>
      </section>

      {/* Role Transition */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">リーダーの本質的役割</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-300">
                <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${roleTransition.wrong.color} rounded-xl mb-4`}>
                  <i className={`${roleTransition.wrong.icon} text-2xl text-white`}></i>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">{roleTransition.wrong.title}</h3>
                  <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">❌</span>
                </div>
                <p className="text-gray-700 mb-6">{roleTransition.wrong.description}</p>
                <ul className="space-y-3">
                  {roleTransition.wrong.characteristics.map((char, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-600">
                      <i className="ri-close-line text-gray-400 mt-1 flex-shrink-0"></i>
                      <span>{char}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-rose-300">
                <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${roleTransition.correct.color} rounded-xl mb-4 shadow-md`}>
                  <i className={`${roleTransition.correct.icon} text-2xl text-white`}></i>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">{roleTransition.correct.title}</h3>
                  <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-sm font-medium">⭕</span>
                </div>
                <p className="text-gray-700 mb-6">{roleTransition.correct.description}</p>
                <ul className="space-y-3">
                  {roleTransition.correct.characteristics.map((char, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <i className="ri-checkbox-line text-rose-600 mt-1 flex-shrink-0"></i>
                      <span>{char}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specific Actions */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">5つの具体的行動</h2>
            <div className="space-y-12">
              {specificActions.map((action, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg border border-rose-100 overflow-hidden">
                  <div className={`bg-gradient-to-r ${action.color} p-6`}>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl">
                        <i className={`${action.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="flex items-center justify-center w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold">
                            {action.number}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold text-white">{action.title}</h3>
                        <p className="text-white/90 mt-2">{action.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="bg-rose-50 rounded-xl p-6 mb-6">
                      <h4 className="text-lg font-bold text-rose-900 mb-2 flex items-center gap-2">
                        <i className="ri-question-line"></i>
                        なぜ重要か
                      </h4>
                      <p className="text-gray-700">{action.why}</p>
                    </div>

                    <h4 className="text-xl font-bold text-gray-900 mb-4">実践方法</h4>
                    <ul className="space-y-3 mb-6">
                      {action.practices.map((practice, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <i className="ri-checkbox-circle-line text-rose-600 mt-1 flex-shrink-0"></i>
                          <span>{practice}</span>
                        </li>
                      ))}
                    </ul>

                    <h4 className="text-xl font-bold text-gray-900 mb-4">具体的な言葉の例</h4>
                    <div className="space-y-3">
                      {action.examples.map((example, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-4 border-rose-500">
                          <p className="text-gray-700 italic">&ldquo;{example}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4">明日の一手</h2>
                  <p className="text-lg leading-relaxed mb-6">
                    まずは「①自分の不完全さを認める」から始めましょう。次回のミーティングで、あなたが今困っていることや分からないことを正直に話してみてください。リーダーが脆弱性を開示することで、メンバーも安心して本音を話せるようになります。
                  </p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg inline-flex whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    <span>所要時間：5〜10分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link
                to="/team-building/psychological-safety"
                className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                <span>前へ：心理的安全性</span>
              </Link>
              <Link
                to="/team-building/success-factors"
                className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                <span>次へ：成果最大化の条件</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
